import { ListItemIcon, ListItemText, MenuItem, Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import inflection from "inflection";
import React, { useState } from "react";
import { Confirm, useRefresh, useTranslate } from "react-admin";
import {
  TenantRegistrationApiFactory,
  TenantRegistrationDto,
  TenantRegistrationStatus,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";

interface Props {
  label: string;
  icon: JSX.Element;
  record?: TenantRegistrationDto;
  resource?: string;
  confirmTitle?: string;
  confirmContent?: string;
  onClick?: (event: React.MouseEvent) => void;
  hidden?: boolean | ((record: TenantRegistrationDto) => boolean);
}

const isBoolean = (
  input?: boolean | ((record: TenantRegistrationDto) => boolean)
): input is boolean => {
  return input === true || input === false;
};

const getNewStatus = (record: TenantRegistrationDto) => {
  if (
    record.tenantRegistrationStatus === TenantRegistrationStatus.DeletePending
  ) {
    return TenantRegistrationStatus.DeleteRejected;
  }

  if (
    record.tenantRegistrationStatus ===
    TenantRegistrationStatus.RegistrationPending
  ) {
    return TenantRegistrationStatus.RegistrationRejected;
  }

  if (
    record.tenantRegistrationStatus ===
    TenantRegistrationStatus.DeactivationPending
  ) {
    return TenantRegistrationStatus.DeactivationRejected;
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  reject: {
    color: theme.palette.error.main,
  },
}));

export const RejectTenantRegistrationMenuItem = ({
  label,
  record,
  resource,
  confirmTitle,
  confirmContent,
  onClick,
  hidden,
  icon,
  ...rest
}: Props) => {
  const refresh = useRefresh();
  const translate = useTranslate();
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const axios = useAxiosInstance();
  const classes = useStyles();
  if (!confirmTitle) {
    confirmTitle = "resources.master-tenant-registrations.labels.reject_title";
  }

  if (!confirmContent) {
    confirmContent =
      "resources.master-tenant-registrations.labels.reject_content";
  }

  if (!record) {
    return null;
  }

  if (isBoolean(hidden)) {
    if (hidden) {
      return null;
    }
  } else {
    if (hidden && hidden(record)) {
      return null;
    }
  }

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleReject = () => {
    if (!axios) {
      return;
    }

    setLoading(true);
    TenantRegistrationApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppTenantregistrationApproveordenypendingrequestPost({
        id: `${record.id}`,
        status: getNewStatus(record),
      })
      .then(() => {
        refresh();
      })
      .catch((e) => console.error(e))
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <MenuItem
        {...rest}
        className="actions-menu-item"
        onClick={(e) => {
          setOpen(true);
          if (typeof onClick === "function") {
            onClick(e);
          }
        }}>
        <ListItemIcon className={classes.reject}>{icon}</ListItemIcon>
        <ListItemText>{translate(label, { _: label })}</ListItemText>
      </MenuItem>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={confirmTitle}
        content={confirmContent}
        translateOptions={{
          name: inflection.singularize(
            translate(`resources.${resource}.forcedCaseName`, {
              smart_count: 1,
              _: inflection.humanize(
                translate(`resources.${resource}.name`, {
                  smart_count: 1,
                  _: inflection.singularize(resource!),
                }),
                true
              ),
            })
          ),
          id: record.id,
        }}
        onConfirm={handleReject}
        onClose={handleDialogClose}
      />
    </>
  );
};
